import _ from 'underscore';
import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import { Button } from '@fluentui/react-components';
import { MessageBar, MessageBarBody, MessageBarTitle, MessageBarActions } from '@fluentui/react-message-bar';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';

export const LinkedInMemberTokenWarningBanner = () => {
  const i18n = useContext(I18nContext) as any;
  const { scope } = useContext(ScopeContext) as any; // Use the ScopeContext to access the scope object
  const { cid, aid } = scope.values;
  const instrumentation = useContext(InstrumentationContext) as any;

  const [isLoading, setIsLoading] = useState(true);
  const [isTokenRevoked, setIsTokenRevoked] = useState(false);

  const log = useMemo(() => {
    if (instrumentation && instrumentation.campaignOverviewEdit) {
      const scenario = instrumentation.campaignOverviewEdit.create();
      return scenario.campaignStatusEdit.create();
    }
    return {
      trace: _.noop,
      error: _.noop,
    };
  }, [instrumentation]);

  const getConnectedLinkedInCompanyPage = useCallback(() => edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .LinkedInCompanyPages['Default.GetConnectedLinkedInCompanyPage']
    .$makeCall({
      bypassCache: true,
    })
    .toPromise()
    .catch((err) => {
      log.error('[LinkedInCampaigns][MemberTokenWarningBanner] Failed to fetch LinkedIn company info:', err);
      return {};
    }), [cid, aid, log]);

  useEffect(() => {
    log.trace('[LinkedInCampaigns][MemberTokenWarningBanner] Fetching LinkedIn company info...');

    getConnectedLinkedInCompanyPage()
      .then((companyInfo) => {
        const isMemberTokenRevoked = companyInfo && companyInfo.IsMemberTokenActive === false;
        setIsTokenRevoked(isMemberTokenRevoked);
      })
      .catch((err) => {
        log.error('[LinkedInCampaigns][MemberTokenWarningBanner] Failed to fetch LinkedIn company info:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [aid, cid, log, getConnectedLinkedInCompanyPage]);

  const navigateToReAuthFlow = useCallback(() => {
    log.trace('[LinkedInCampaigns][MemberTokenWarningBanner] Navigating to re-auth flow...');
    scope.append({ isReAuth: true });
    scope.remove(['campaignId', 'adGroupId', 'adId']);
    scope.save('/campaign/vnext/createcampaign/linkedin');
  }, [scope, log]);

  const memberTokenWarning = useMemo(
    () =>
      !isLoading && isTokenRevoked && (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>{i18n.getString(_TL_('Your LinkedIn authentication has been revoked.'))}</MessageBarTitle>
            {i18n.getString(_TL_('Please re-authenticate to continue using LinkedIn features.'))}
          </MessageBarBody>
          <MessageBarActions>
            <Button
              appearance="primary"
              size="small"
              onClick={navigateToReAuthFlow}
              aria-label={i18n.getString(_TL_('Re-authenticate'))}
            >
              {i18n.getString(_TL_('Click here'))}
            </Button>
          </MessageBarActions>
        </MessageBar>
      ),
    [isLoading, isTokenRevoked, i18n, navigateToReAuthFlow]
  );

  return memberTokenWarning || null;
};
